/* ==========================================================================
   FORG3D — Add-On Picker rule engine (PURE. No DOM. No dependencies.)

   The single source of truth for "what does the cart prompt for?". Runs in the
   browser (global FORG3DAddonRules) and in Node (module.exports) so it is unit-
   tested independently of any UI. See specs/2026-08-14-addon-picker-design.md.

   evaluateCart(cartState) -> decision[]

     cartState = {
       lines: [{
         key, sku, quantity,
         isAddon,            // true = this line IS an add-on (never prompts)
         addonFor,           // parent line key when isAddon
         group,              // group_key this line prompts for, or null
         options: [{ productId, variantId, label, sku, priceEach, blurb }],
         recommendedId,      // productId to badge, or null
         suppressesGroups    // group_keys this line's presence cancels
       }],
       groups: { <group_key>: {
         mode,               // 'always' | 'unless_suppressed'
         link_label, prompt_label, prompt_sub, none_label,
         alt_action_label, alt_action_url, recommended_label
       } }
     }

   Returns one decision per triggering line (a line with a known group that is
   not itself an add-on):

     { lineKey, groupKey, mode,
       link_label, prompt_label, prompt_sub, none_label,
       alt_action_label, alt_action_url, recommended_label,
       options, recommendedId,
       satisfied,           // its add-on is already in the cart
       suppressed }         // null | { byLineKey, group }

   All brand/product specifics (which SKUs prompt, what is recommended) live in
   the data the serializer feeds this function — never here.
   ========================================================================== */
(function (root, factory) {
  var api = factory();
  if (root) { root.FORG3DAddonRules = api; }
  if (typeof module !== 'undefined' && module.exports) { module.exports = api; }
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  /* Shopify line keys are "<variantId>:<hash>", and the hash is NOT stable: it
     folds in the line's discount allocations. So completing a bundle rewrites
     the parent's key — which is exactly what attaching an add-on does when the
     pair triggers an automatic discount (SYS-FIT TRK + SYS-PLT Pro Maximus).
     The child's _addon_for, captured a moment earlier, would then point at a
     key that no longer exists: the child orphans, the parent re-prompts, and
     the customer can add a duplicate.

     Pair on the variant-id half, which does not rotate. Two separate lines of
     the SAME parent variant would both see one add-on as theirs; that is a far
     rarer case than the discount rotation, and it fails toward "already
     handled" rather than toward double-charging. */
  function lineRef(key) {
    return typeof key === 'string' ? key.split(':')[0] : '';
  }
  function sameLine(a, b) {
    if (!a || !b) { return false; }
    if (a === b) { return true; }
    var ra = lineRef(a);
    return ra !== '' && ra === lineRef(b);
  }

  function evaluateCart(cartState) {
    if (!cartState || typeof cartState !== 'object') { return []; }
    var lines = Array.isArray(cartState.lines) ? cartState.lines : [];
    var groups = cartState.groups && typeof cartState.groups === 'object' ? cartState.groups : {};

    // Which groups are cancelled by something present in the cart?
    // Map group_key -> the line key that suppresses it (first wins).
    var suppressorOf = {};
    lines.forEach(function (line) {
      var list = line && Array.isArray(line.suppressesGroups) ? line.suppressesGroups : [];
      list.forEach(function (g) {
        if (!(g in suppressorOf)) { suppressorOf[g] = line.key; }
      });
    });

    var decisions = [];

    lines.forEach(function (line) {
      if (!line || line.isAddon) { return; }            // add-on lines never prompt
      if (!line.group) { return; }                       // nothing to offer
      var group = groups[line.group];
      if (!group) { return; }                            // unknown group — stay silent

      var suppressed = null;
      if (group.mode === 'unless_suppressed' && (line.group in suppressorOf)) {
        suppressed = { byLineKey: suppressorOf[line.group], group: line.group };
      }

      var satisfied = lines.some(function (l) {
        return l && l.isAddon && sameLine(l.addonFor, line.key);
      });

      var options = Array.isArray(line.options) ? line.options : [];
      var recommendedId = null;
      if (line.recommendedId != null) {
        var isValid = options.some(function (o) { return o && o.productId === line.recommendedId; });
        if (isValid) { recommendedId = line.recommendedId; }
      }

      decisions.push({
        lineKey: line.key,
        groupKey: line.group,
        mode: group.mode,
        link_label: group.link_label || '',
        prompt_label: group.prompt_label || '',
        prompt_sub: group.prompt_sub || '',
        none_label: group.none_label || '',
        alt_action_label: group.alt_action_label || '',
        alt_action_url: group.alt_action_url || '',
        recommended_label: group.recommended_label || 'Recommended',
        options: options,
        recommendedId: recommendedId,
        satisfied: satisfied,
        suppressed: suppressed
      });
    });

    return decisions;
  }

  return { evaluateCart: evaluateCart, lineRef: lineRef, sameLine: sameLine };
});
